import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter } from "recharts";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface PenguinData {
  species: string;
  island: string;
  bill_length_mm: number;
  bill_depth_mm: number;
  flipper_length_mm: number;
  body_mass_g: number;
  sex: string;
}

const PenguinExplorer = () => {
  const { t } = useLanguage();
  const { session } = useAuth();
  const [penguinData, setPenguinData] = useState<PenguinData[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [thinking, setThinking] = useState("");
  const [answer, setAnswer] = useState("");
  const [showThinking, setShowThinking] = useState(false);

  const questions = [
    t.portfolio.penguinExplorer.questions[0],
    t.portfolio.penguinExplorer.questions[1],
    t.portfolio.penguinExplorer.questions[2],
  ];

  useEffect(() => {
    fetch("/data/penguins_cleaned.csv")
      .then((response) => response.text())
      .then((text) => {
        const rows = text.split("\n").slice(1);
        const data = rows
          .filter((row) => row.trim())
          .map((row) => {
            const cols = row.split(",");
            return {
              species: cols[0],
              island: cols[1],
              bill_length_mm: parseFloat(cols[2]),
              bill_depth_mm: parseFloat(cols[3]),
              flipper_length_mm: parseFloat(cols[4]),
              body_mass_g: parseFloat(cols[5]),
              sex: cols[6],
            };
          });
        setPenguinData(data);
        setSelectedQuestion(questions[0]);
      })
      .catch((err) => {
        console.error("Error loading penguin data:", err);
        toast.error(t.portfolio.penguinExplorer.errorLoading);
      });
  }, []);

  const handleSubmit = async () => {
    if (!selectedQuestion) return;

    setIsLoading(true);
    setThinking("");
    setAnswer("");
    setShowThinking(true);

    try {
      const headers: Record<string, string> = {};
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }

      const { data, error } = await supabase.functions.invoke("analyze-penguins", {
        body: {
          question: selectedQuestion,
          data: penguinData.slice(0, 20),
        },
        headers
      });

      if (error) {
        console.error("Analysis error:", error);
        if (error.message?.includes('Rate limit')) {
          toast.error(t.portfolio.penguinExplorer.rateLimitError);
        } else if (error.message?.includes('Payment required')) {
          toast.error(t.portfolio.penguinExplorer.paymentError || "Payment required. Please add credits.");
        } else {
          toast.error(t.portfolio.penguinExplorer.errorAnalyzing);
        }
        return;
      }

      setThinking(data.thinking || "");
      setAnswer(data.answer || "");
    } catch (error) {
      console.error("Error:", error);
      toast.error(t.portfolio.penguinExplorer.errorAnalyzing);
    } finally {
      setIsLoading(false);
    }
  };

  const speciesCount = penguinData.reduce((acc, penguin) => {
    acc[penguin.species] = (acc[penguin.species] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const chartData = Object.entries(speciesCount).map(([species, count]) => ({
    species,
    count,
  }));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🐧 {t.portfolio.penguinExplorer.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              {t.portfolio.penguinExplorer.selectQuestion}
            </label>
            <Select value={selectedQuestion} onValueChange={setSelectedQuestion}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {questions.map((q, i) => (
                  <SelectItem key={i} value={q}>
                    {q}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button onClick={handleSubmit} disabled={isLoading} className="w-full">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t.portfolio.penguinExplorer.analyzing}
              </>
            ) : (
              t.portfolio.penguinExplorer.submit
            )}
          </Button>

          {thinking && (
            <Card className="bg-muted/50">
              <CardHeader
                className="cursor-pointer flex flex-row items-center justify-between py-3"
                onClick={() => setShowThinking(!showThinking)}
              >
                <span className="text-sm font-medium">
                  {t.portfolio.penguinExplorer.thinking}
                </span>
                {showThinking ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </CardHeader>
              {showThinking && (
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {thinking}
                  </p>
                </CardContent>
              )}
            </Card>
          )}

          {answer && (
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <p className="text-sm whitespace-pre-wrap">{answer}</p>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {t.portfolio.penguinExplorer.speciesDistribution}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="species" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {t.portfolio.penguinExplorer.billVsBody}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bill_length_mm" name="Bill Length (mm)" />
                <YAxis dataKey="body_mass_g" name="Body Mass (g)" />
                <Tooltip cursor={{ strokeDasharray: "3 3" }} />
                <Legend />
                <Scatter
                  name="Penguins"
                  data={penguinData}
                  fill="hsl(var(--primary))"
                />
              </ScatterChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t.portfolio.penguinExplorer.dataTable}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Species</th>
                  <th className="text-left p-2">Island</th>
                  <th className="text-right p-2">Bill Length</th>
                  <th className="text-right p-2">Body Mass</th>
                  <th className="text-right p-2">Flipper Length</th>
                </tr>
              </thead>
              <tbody>
                {penguinData.slice(0, 10).map((penguin, i) => (
                  <tr key={i} className="border-b hover:bg-muted/50">
                    <td className="p-2">{penguin.species}</td>
                    <td className="p-2">{penguin.island}</td>
                    <td className="text-right p-2">{penguin.bill_length_mm}</td>
                    <td className="text-right p-2">{penguin.body_mass_g}</td>
                    <td className="text-right p-2">{penguin.flipper_length_mm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PenguinExplorer;
